import { useTranslation } from 'react-i18next';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  flowListFilterSetAssignedOnly,
  flowListFilterSetShowPending,
  flowListFilterSetShowRejected,
  flowListFilterSetExtra,
  flowListFilterSetTrayView
} from '../actions/flowListFilterActions';
import {
  shouldShowFlowListFilter,
  isFlowModuleListFilterActive,
  resolveFlowListRequestParams
} from '../helpers/flowModuleListQueryHelper';
import { defaultTrayView, resolveTrayTabMode } from '../helpers/flowTraySurfaceHelper';
import useActiveSpecialistRole, { getActiveSpecialistRoleLabel } from './useActiveSpecialistRole';
import { resolveGenerationPhaseLabel } from '../helpers/flowEditPageTitleHelper';

const useFlowModuleListFilters = ({
  process,
  isRoleSpecialistSelected,
  onReload,
  pageInitial = 0,
  itemsPerPage = 10,
  onFilterChange
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const configuration = useSelector((state) => state.configuration);
  const roleLogin = useSelector((state) => state.userRole?.roleLogin);
  const assignedOnly = useSelector((state) => state.flowListFilter?.assignedOnly !== false);
  const showPending = useSelector((state) => state.flowListFilter?.showPending === true);
  const showRejected = useSelector((state) => state.flowListFilter?.showRejected === true);
  const trayView = useSelector((state) => state.flowListFilter?.trayView || 'mine');
  const extra = useSelector((state) => state.flowListFilter?.extra || {});

  const listFilterState = useMemo(
    () => ({
      configuration,
      userRole: { roleLogin },
      flowListFilter: { assignedOnly, showPending, showRejected, extra, trayView }
    }),
    [configuration, roleLogin, assignedOnly, showPending, showRejected, extra, trayView]
  );

  const {
    selectedRole,
    roleKey,
    isRoleSelected
  } = useActiveSpecialistRole();

  const showFlowListFilter = shouldShowFlowListFilter(() => listFilterState, process);
  const flowFilterActive = isFlowModuleListFilterActive(() => listFilterState, process);
  const resolvedParams = resolveFlowListRequestParams(() => listFilterState, process);
  const showUserFilter = resolvedParams?.showUserFilter === true;
  const tabMode = resolveTrayTabMode(roleKey);
  const roleLabel = getActiveSpecialistRoleLabel(selectedRole, t);
  const titleSuffix = resolveGenerationPhaseLabel(selectedRole, t);
  const LIST_ROLE_STORAGE = 'teccc.flowList.lastRole';

  useEffect(() => {
    if (roleKey) {
      const storedRole = sessionStorage.getItem(LIST_ROLE_STORAGE) || '';
      if (storedRole !== roleKey) {
        sessionStorage.setItem(LIST_ROLE_STORAGE, roleKey);
        dispatch(flowListFilterSetTrayView(defaultTrayView(roleKey)));
        const hasExtra = Object.values(extra || {}).some((value) => String(value || '').trim());
        if (hasExtra) {
          dispatch(flowListFilterSetExtra({}));
          return;
        }
      }
    }
    if (typeof onReload !== 'function') {
      return;
    }
    onFilterChange?.();
    onReload(pageInitial, itemsPerPage);
  }, [assignedOnly, showPending, showRejected, extra, trayView, roleKey, selectedRole?.id, isRoleSelected, isRoleSpecialistSelected, process]);

  return {
    assignedOnly,
    showPending,
    showRejected,
    trayView,
    tabMode,
    showFlowListFilter,
    flowFilterActive,
    showUserFilter,
    roleLabel,
    titleSuffix,
    selectedRole,
    extra,
    setAssignedOnly: (checked) => dispatch(flowListFilterSetAssignedOnly(checked)),
    setShowPending: (checked) => dispatch(flowListFilterSetShowPending(checked)),
    setShowRejected: (checked) => dispatch(flowListFilterSetShowRejected(checked)),
    setTrayView: (nextView) => dispatch(flowListFilterSetTrayView(nextView)),
    setExtra: (next) => dispatch(flowListFilterSetExtra(next))
  };
};

export default useFlowModuleListFilters;
